/**
 * Created by rathompson on 5/23/22
 * File: AssociateAppTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useContext, useMemo, useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  ConnectedPaginator,
  ConnectedTable,
  ConnectedTableLayout,
  TableProvider
} from '@panwds/react-table'
import { ExclamationTriangleIcon } from '@panwds/icons'
import FiltersBar from '../../../common/table/FiltersBar'
import AppContext from '../../../../../common/context/AppContext'
import { isEqual } from 'lodash'
import { ARRAY } from '../../../../../common/utils/Utils'
import V5TableWrapper from '../../../../../common/components/table/V5TableWrapper'
import { tableColumns } from '../associationTableUtils'
import { customRowDisableHook } from '../../../common/table/tableUtil'

const AssociateAppTable = ({
  data,
  loading,
  tableRef: _tableRef,
  setSelected: _setSelected,
  selectedApp,
  hasFirewallLicense,
  licenseLoader,
  error
}) => {

  const { apps, getAppName } = useContext(AppContext)


  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const totalRecords = useMemo(() => data?.length || 0, [data])
  const [selected, setSelected] = useState([])
  const [devices, setDevices] = useState([])

  const ref = _tableRef
  const tableRef = useCallback((node) => {
    if (node) {
      ref.current = node
      const _selected = []
      for (const row of node.selectedFlatRows) {
        _selected.push(row.original)
      }
      if (!isEqual(_selected, selected)) {
        setSelected(_selected)
      }
    }
  }, [ref, selected])

  useEffect(() => {
    if (_setSelected) {
      _setSelected(selected)
    }
  }, [_setSelected, selected])

  useEffect(() => {
    const selectedAppInfo = apps[selectedApp?.app_id_display_override || selectedApp?.app_id]
    if (selectedAppInfo) {
      const { app_id, device_association } = selectedAppInfo
      const devicesData = data?.map(each => {
        const appName = getAppName(app_id)
        each.isRestricted = false // reset
        each.disableReason = undefined //reset
        // device is not eligible for license upgrade and already app associated
        if (!each.isUpgrade && each?.associations?.some(({ app_id: id }) => id === app_id)) {
          each.disableReason = 'Device is already associated with the application'
          each.isRestricted = true
        }
        else if (ARRAY.hasArray(device_association?.restricted_accounts) && !device_association?.restricted_accounts.includes(Number(each?.support_account_id))) {
          each.disableReason = `Your customer support account does not have access to associate devices with ${appName}.`
          each.isRestricted = true
        }
        //device_association?.single_support_account
        else if (each.support_account_id !== selectedApp?.support_account_id) {
          each.disableReason = `The device must be registered with the same customer support account used to activate ${appName}.`
          each.isRestricted = true
        }
        return each
      })
      setDevices(devicesData)
    }
    else {
      setDevices([])
    }
  }, [selectedApp, apps, data, getAppName])

  const onPaginationChange = useCallback(async ({ pageIndex: page, pageSize: size }) => {
    if (pageIndex !== page || pageSize !== size) {
      setPageIndex(page)
      setPageSize(size)
    }
  }, [pageIndex, pageSize])

  useEffect(() => {
    let index = pageIndex
    let size = pageSize
    while ((index * size) >= totalRecords) {
      --index
      --size
    }
    setPageIndex(index)
    setPageSize(size)
  }, [pageIndex, pageSize, totalRecords])

  const useControlledState = useCallback((state, { instance }) => {
    const selectedRowIds = Object.keys(state.selectedRowIds)
      .map((index) => ({ index, device: instance?.data[Number(index)] }))
      .filter(({ index, device }) => {
        return Boolean(device)
      })
      .reduce((map, row) => {
        map[row.index] = true
        return map
      }, {})
    return Object.assign({}, state, { selectedRowIds })
  }, [])

  const emptyDataDescription = useMemo(() => {
    if (!selectedApp) {
      return 'To view the devices, please select the product from the left panel.'
    }
    else if (error) {
      return <ExclamationTriangleIcon className='tw-text-icon-red'/>
    }
    return 'No Devices Available'
  }, [error, selectedApp])
  return (
    <V5TableWrapper className='tw-w-full tw-h-full tw-flex tw-flex-col'>
      <TableProvider
        isLoading={hasFirewallLicense && licenseLoader}
        tableInstanceRef={tableRef}
        columns={tableColumns(hasFirewallLicense, selectedApp)}
        data={devices}
        enablePagination
        enableRowSelect
        enableResizeColumns
        enableColumnOrder
        enableColumnSort
        isBackgroundFetching={loading}
        initialState={{
          pageIndex: pageIndex,
          pageSize
        }}
        onPaginationChange={onPaginationChange}
        pageCount={Math.ceil(totalRecords / pageSize)}
        useControlledState={useControlledState}
        overrideHooks={[customRowDisableHook]}
      >
        <FiltersBar/>
        <ConnectedTableLayout
          paginator={<ConnectedPaginator pageSizeOptions={[25]}/>} //due to casb-x flow limitation
          overflow
        >
          <ConnectedTable emptyStateProps={{
            heading: emptyDataDescription,
          }}/>
        </ConnectedTableLayout>
      </TableProvider>
    </V5TableWrapper>
  )
}

AssociateAppTable.propTypes = {
  header: PropTypes.element,
  data: PropTypes.array.isRequired
}

AssociateAppTable.defaultProps = {
  data: []
}
export default AssociateAppTable
